import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { Subscriptions_ProductType as ProductType } from '__generated__/graphql-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useLocation } from 'js/lib/useRouter';
import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay, momentWithUserTimezone } from 'js/utils/DateTimeUtils';
import type { MomentFormat } from 'js/utils/DateTimeUtils';

import type { ProductType as EventingProductType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';
import CancelSubscription from 'bundles/my-purchases-graphql/components/manage-subscriptions/queries/CancelSubscriptionMutation.graphql';
import type {
  CancelSubscriptionMutation,
  CancelSubscriptionMutationVariables,
} from 'bundles/my-purchases-graphql/components/manage-subscriptions/queries/__generated__/CancelSubscriptionMutation';
import { useGetLoggedInUserSubscriptions } from 'bundles/my-purchases-graphql/components/utils/useGetLoggedInUserSubscriptions';
import { useNaptime } from 'bundles/naptimejs';
import CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import PaymentProcessorTypes from 'bundles/payments/common/PaymentProcessorTypes';
import SubscriptionStatus from 'bundles/subscriptions/common/SubscriptionStatus';
import GetS12nNameById from 'bundles/subscriptions/queries/GetS12nNameByIdQuery.graphql';
import QueryMySubscriptionRestorationEligibility from 'bundles/subscriptions/queries/QueryMySubscriptionRestorationEligibility.graphql';
import RestoreCanceledSubscription from 'bundles/subscriptions/queries/RestoreCanceledSubscriptionMutation.graphql';
import type {
  GetS12nNameByIdQuery,
  GetS12nNameByIdQueryVariables,
} from 'bundles/subscriptions/queries/__generated__/GetS12nNameByIdQuery';
import type {
  QueryMySubscriptionRestorationEligibilityQuery,
  QueryMySubscriptionRestorationEligibilityQueryVariables,
} from 'bundles/subscriptions/queries/__generated__/QueryMySubscriptionRestorationEligibility';
import type {
  RestoreCanceledSubscriptionMutation,
  RestoreCanceledSubscriptionMutationVariables,
} from 'bundles/subscriptions/queries/__generated__/RestoreCanceledSubscriptionMutation';
import isGoogleMarketingPage from 'bundles/unified-description-page-common/utils/isGoogleMarketingPage';

import _t from 'i18n!nls/subscriptions';

export const DISMISSED_SUBSCRIPTION_IDS_STORAGE_KEY =
  'canceled_subscription_reactivate_notification_banner_dismissed_subscription_ids';

export const isCancellationUndoEnabled = () => GoogleCertificateEpicClient.preview('cancellationUndoEnabled');

export const generateImpressionForCancellationUndo = (isEligibleForUndoCancellation: boolean) => {
  if (isEligibleForUndoCancellation) {
    GoogleCertificateEpicClient.get('cancellationUndoEnabled');
  }
};

export const useCancelSubscription = () => {
  const [executeMutation, mutationState] = useMutation<CancelSubscriptionMutation, CancelSubscriptionMutationVariables>(
    CancelSubscription,
    { context: { clientName: 'gatewayGql' } }
  );

  const cancelSubscription = (subscriptionId: string) => {
    return executeMutation({
      variables: {
        input: {
          subscriptionId,
        },
      },
    });
  };

  return {
    cancelSubscription,
    isLoading: mutationState.loading,
    error: mutationState.error,
  };
};

export const useReactivateCanceledSubscription = () => {
  const [executeMutation, mutationState] = useMutation<
    RestoreCanceledSubscriptionMutation,
    RestoreCanceledSubscriptionMutationVariables
  >(RestoreCanceledSubscription, { context: { clientName: 'gatewayGql' } });

  const reactivateSubscription = (subscriptionId: string) => {
    return executeMutation({
      variables: {
        input: {
          subscriptionId,
        },
      },
    });
  };

  return {
    reactivateSubscription,
    isLoading: mutationState.loading,
    error: mutationState.error,
  };
};

export const isCourseraPlusOrIsSpecializationSubscription = (subscription: SubscriptionsV1) => {
  const { isCourseraPlusSubscription, isSpecializationSubscription } = subscription || {};
  return isCourseraPlusSubscription || isSpecializationSubscription;
};

const isStripeSubscription = (subscription?: SubscriptionsV1) => {
  const { paymentProcessorId } = subscription || {};

  return Boolean(
    paymentProcessorId &&
      [
        PaymentProcessorTypes.stripeConsumer,
        PaymentProcessorTypes.stripeConsumerSandbox,
        PaymentProcessorTypes.stripeBvConsumer,
        PaymentProcessorTypes.stripeBvConsumerSandbox,
      ].includes(paymentProcessorId)
  );
};

export const isEligibleForStripeCancellation = (subscription: SubscriptionsV1) => {
  const { status, isCourseraPlusSubscription, isSpecializationSubscription } = subscription || {};

  const isActiveOrFreeTrial = status === SubscriptionStatus.ACTIVE || status === SubscriptionStatus.FREE_TRIAL;

  const isEligibleSubscription = isActiveOrFreeTrial && (isCourseraPlusSubscription || isSpecializationSubscription);

  // Only Stripe subscriptions
  return Boolean(isEligibleSubscription && isStripeSubscription(subscription));
};

export const isEligibleForUndoCancellation = (subscription?: SubscriptionsV1) => {
  const { isCourseraPlusSubscription, isSpecializationSubscription, isCancelled } = subscription || {};
  const isEligibleSubscription = isCancelled && (isCourseraPlusSubscription || isSpecializationSubscription);

  return Boolean(isEligibleSubscription && isStripeSubscription(subscription));
};

export const useGetEligibleSubscriptions = ({ skip }: { skip: boolean }) => {
  const { data, loading } = useQuery<
    QueryMySubscriptionRestorationEligibilityQuery,
    QueryMySubscriptionRestorationEligibilityQueryVariables
  >(QueryMySubscriptionRestorationEligibility, {
    context: { clientName: 'gatewayGql' },
    skip,
  });

  return { eligibleSubscriptionsData: data?.SubscriptionsQueries?.queryMySubscriptionRestorationEligibility, loading };
};

const useGetS12nNameById = (s12nId?: string) => {
  const { data, loading } = useQuery<GetS12nNameByIdQuery, GetS12nNameByIdQueryVariables>(GetS12nNameById, {
    context: { clientName: 'gatewayGql' },
    variables: { id: s12nId! },
    skip: !s12nId,
  });

  return {
    s12nName: data?.Specialization?.queryById?.name,
    loading,
  };
};

const getFutureDateDifferenceString = (dateString?: string): string | null => {
  if (!dateString) {
    return null;
  }
  const todayDate = momentWithUserTimezone();
  const timestampDate = momentWithUserTimezone(dateString).locale(_t.getLocale()).utc();

  const daysDifference = Math.round(timestampDate.diff(todayDate, 'days', true));
  const monthsDifference = Math.round(timestampDate.diff(todayDate, 'months', true));

  if (daysDifference <= 0) {
    return _t('today');
  } else if (daysDifference === 1) {
    return _t('tomorrow');
  } else if (daysDifference < 30) {
    return _t('in #{daysDifference} days', { daysDifference });
  } else if (monthsDifference === 1) {
    return _t('in 1 month');
  } else {
    return _t('in #{monthsDifference} months', { monthsDifference });
  }
};

type PropsFromNaptime = {
  cart?: CartsV2;
};

const getDisplayedDateInfo = (
  restorationEligibilityEndsAt?: string,
  formatString: MomentFormat = LONG_DATE_ONLY_DISPLAY
) => {
  if (!restorationEligibilityEndsAt) {
    return null;
  }

  const futureDateDifferenceString = getFutureDateDifferenceString(restorationEligibilityEndsAt);
  // Next subscription starts a day after the previous ends
  const nextBillingAt = restorationEligibilityEndsAt && momentWithUserTimezone(restorationEligibilityEndsAt);
  const restorationEligibilityEndDate = restorationEligibilityEndsAt
    ? formatDateTimeDisplay(restorationEligibilityEndsAt, formatString)
    : null;
  const nextBillingDate = nextBillingAt && formatDateTimeDisplay(nextBillingAt, formatString);

  return {
    futureDateDifferenceString,
    restorationEligibilityEndDate,
    nextBillingDate,
  };
};

const useGetMonthlySubscriptionPrice = (subscription?: SubscriptionsV1) => {
  const {
    data: { cart },
    pending,
  } = useNaptime<PropsFromNaptime>(() => {
    if (!subscription || !subscription.cartId) {
      return {};
    }

    return {
      cart: CartsV2.get(subscription.cartId),
    };
  }, [subscription?.cartId]);

  const monthlySubscriptionPrice = cart && {
    amount: cart.originalTotalCartAmount,
    currencyCode: cart.currencyCode,
  };

  return {
    monthlySubscriptionPrice,
    pending,
  };
};

type ButtonName = 'reactivate_subscription_confirm' | 'reactivate_subscription_cta';

export const useEventTracking = (subscription?: SubscriptionsV1, eligibleSubscriptionIds?: string[]) => {
  const track = useTracker();

  const reactivateEventingV2Props = subscription
    ? {
        subscriptionId: subscription.id,
        eligibleSubscriptionIds: JSON.stringify(eligibleSubscriptionIds ?? []),
      }
    : {};

  const getEventingV3Props = (name: ButtonName) => {
    return {
      button: {
        name,
      },
      ...(subscription
        ? {
            product: {
              id: subscription.productItemId,
              type: (subscription.isCourseraPlusSubscription ? 'cplus' : 's12n') as EventingProductType,
            },
          }
        : {}),
    };
  };

  const trackClickButton = (name: ButtonName) => {
    if (!subscription) {
      return;
    }

    track('click_button', getEventingV3Props(name));
  };

  const trackClickReactivateCta = () => trackClickButton('reactivate_subscription_cta');
  const trackClickReactivateConifrm = () => trackClickButton('reactivate_subscription_confirm');

  const useGetButtonTrackingRef = (name: ButtonName) => {
    const visibilityTrackingRef: React.MutableRefObject<any> = useVisibilityTracker(
      'view_button',
      getEventingV3Props(name)
    );

    return visibilityTrackingRef;
  };

  const useGetReactivateCtaTrackingRef = () => useGetButtonTrackingRef('reactivate_subscription_cta');
  const useGetReactivateConfirmTrackingRef = () => useGetButtonTrackingRef('reactivate_subscription_confirm');

  return {
    reactivateEventingV2Props,
    trackClickReactivateCta,
    trackClickReactivateConifrm,
    useGetReactivateCtaTrackingRef,
    useGetReactivateConfirmTrackingRef,
  };
};

// Subject to change in subsequent PRs.
export const useUndoCancellationData = (productItemId: string) => {
  const { eligibleSubscriptionsData } = useGetEligibleSubscriptions({ skip: !isCancellationUndoEnabled() });
  const eligibleSubscriptionIds = eligibleSubscriptionsData?.map((sub) => sub.subscription.id);
  const eligibleSubscription = eligibleSubscriptionsData?.find(
    (sub) => sub.subscription.productId.productItemId === productItemId
  );
  const { subscriptions } = useGetLoggedInUserSubscriptions({ skip: !eligibleSubscription });
  const subscription = subscriptions?.find((sub) => sub.id === eligibleSubscription?.subscription.id);
  const isEligibleSubscription = isEligibleForUndoCancellation(subscription);
  const { monthlySubscriptionPrice } = useGetMonthlySubscriptionPrice(
    isEligibleSubscription ? subscription : undefined
  );

  const restorationEligibilityEndsAt = eligibleSubscription?.restorationEligibilityEndsAt;

  const { nextBillingDate, restorationEligibilityEndDate, futureDateDifferenceString } =
    getDisplayedDateInfo(restorationEligibilityEndsAt, LONG_DATE_ONLY_DISPLAY) || {};

  const getCancelledStatusCopyOnMyPurchases = () => {
    if (!restorationEligibilityEndsAt) {
      return null;
    }
    return (
      <FormattedMessage
        message={_t('Status: Ends {inDaysOrMonths} on {subscriptionEndDate}')}
        inDaysOrMonths={futureDateDifferenceString}
        subscriptionEndDate={restorationEligibilityEndDate}
      />
    );
  };

  return {
    showUndoCancellationCTA: isEligibleSubscription && isCancellationUndoEnabled(),
    subscription,
    getCancelledStatusCopyOnMyPurchases,
    restorationEligibilityEndDate,
    nextBillingDate,
    monthlySubscriptionPrice,
    eligibleSubscriptionIds,
  };
};

export const useUndoCancellationDataForLihp = ({
  skip,
  dismissedSubscriptionIds = [],
}: {
  skip?: boolean;
  dismissedSubscriptionIds: string[];
}) => {
  // skip can get passed in if the learner has already dismissed the banner before
  const { eligibleSubscriptionsData, loading: loadingEligibleSubs } = useGetEligibleSubscriptions({
    skip: skip || !isCancellationUndoEnabled(),
  });
  const eligibleSubscriptionIds = eligibleSubscriptionsData?.map((sub) => sub.subscription.id);

  // Logic for what gets shown is as follows:
  // If the learner has already dismissed the banner for the subscriptionId, don't show banner
  // If the learner has a canceled C+ subscription, show the banner for the C+ subscription
  // Else, show the banner for the s12n subscription ending the soonest
  const nonDismissedEligibleSubscriptionsData = eligibleSubscriptionsData?.filter(
    (sub) => !dismissedSubscriptionIds.includes(sub.subscription.id)
  );
  const eligibleSubscription =
    nonDismissedEligibleSubscriptionsData?.find(
      (sub) => sub.subscription.productId.productType === ProductType.CourseraPlusSubscription
    ) ??
    nonDismissedEligibleSubscriptionsData
      ?.filter((sub) => sub.subscription.productId.productType === ProductType.Specialization)
      ?.sort((subA, subB) => {
        const timestampForSubA = momentWithUserTimezone(subA?.restorationEligibilityEndsAt).valueOf();
        const timestampForSubB = momentWithUserTimezone(subB?.restorationEligibilityEndsAt).valueOf();
        return timestampForSubA - timestampForSubB;
      })?.[0];

  const { subscriptions, pending: loadingSubscription } = useGetLoggedInUserSubscriptions({
    skip: !eligibleSubscription,
  });
  const subscription = subscriptions?.find((sub) => sub.id === eligibleSubscription?.subscription.id);
  const isEligibleSubscription = isEligibleForUndoCancellation(subscription);
  const { monthlySubscriptionPrice, pending: loadingPrice } = useGetMonthlySubscriptionPrice(
    isEligibleSubscription ? subscription : undefined
  );

  const s12nId = !subscription?.isCourseraPlusSubscription ? subscription?.productItemId : undefined;
  const { s12nName, loading: loadingS12n } = useGetS12nNameById(s12nId);

  const restorationEligibilityEndsAt = eligibleSubscription?.restorationEligibilityEndsAt;

  const { nextBillingDate, restorationEligibilityEndDate, futureDateDifferenceString } =
    getDisplayedDateInfo(restorationEligibilityEndsAt, LONG_DATE_ONLY_DISPLAY) || {};

  const getCancelledStatusCopyOnLihp = () => {
    if (!restorationEligibilityEndsAt) {
      return null;
    }
    return (
      <FormattedMessage
        message={_t('Your subscription ends {inDaysOrMonths} on {subscriptionEndDate}')}
        inDaysOrMonths={futureDateDifferenceString}
        subscriptionEndDate={restorationEligibilityEndDate}
      />
    );
  };

  const loading = Boolean(loadingEligibleSubs || loadingSubscription || loadingPrice || loadingS12n);

  return {
    showUndoCancellationCTA: isEligibleSubscription,
    subscription,
    s12nName,
    getCancelledStatusCopyOnLihp,
    restorationEligibilityEndsAt,
    nextBillingDate,
    monthlySubscriptionPrice,
    eligibleSubscriptionIds,
    loading,
  };
};

export const useUndoCancellationDataForXdp = ({
  productItemId,
  isCourseraPlusEligibleProduct,
  partnerIds,
}: {
  productItemId?: string;
  isCourseraPlusEligibleProduct?: boolean;
  partnerIds?: string[];
}) => {
  const { pathname } = useLocation();

  const shouldQueryEligibleSubscriptions = Boolean(
    isCancellationUndoEnabled() && ((productItemId && partnerIds) || isCourseraPlusEligibleProduct)
  );

  const { eligibleSubscriptionsData } = useGetEligibleSubscriptions({ skip: !shouldQueryEligibleSubscriptions });
  const eligibleSubscriptionIds = eligibleSubscriptionsData?.map((sub) => sub.subscription.id);

  // Logic is as follows:
  // If it's a GwG-owned XDP, only display the CTA if learner has that specific s12n subscription
  // Else, for a Coursera-owned XDP, display the CTA if learner has that specific s12n subscription
  // or has a C+ subscription and the product is C+ eligible
  const eligibleCourseraPlusSubscription =
    isCourseraPlusEligibleProduct && !isGoogleMarketingPage(pathname)
      ? eligibleSubscriptionsData?.find(
          (sub) => sub.subscription.productId.productType === ProductType.CourseraPlusSubscription
        )
      : undefined;
  const eligibleS12nSubscription = productItemId
    ? eligibleSubscriptionsData?.find((sub) => sub.subscription.productId.productItemId === productItemId)
    : undefined;
  const eligibleSubscription = eligibleCourseraPlusSubscription ?? eligibleS12nSubscription;

  const { subscriptions } = useGetLoggedInUserSubscriptions({ skip: !eligibleSubscription });
  const subscription = subscriptions?.find((sub) => sub.id === eligibleSubscription?.subscription.id);
  const isEligibleSubscription = isEligibleForUndoCancellation(subscription);
  const { monthlySubscriptionPrice } = useGetMonthlySubscriptionPrice(
    isEligibleSubscription ? subscription : undefined
  );

  const restorationEligibilityEndsAt = eligibleSubscription?.restorationEligibilityEndsAt;
  const { nextBillingDate, futureDateDifferenceString } =
    getDisplayedDateInfo(eligibleSubscription?.restorationEligibilityEndsAt) || {};

  const getCancelledStatusCopyOnXdp = () => {
    if (!restorationEligibilityEndsAt) {
      return null;
    }
    return (
      <FormattedMessage
        message={_t('Your subscription ends {inDaysOrMonths}')}
        inDaysOrMonths={futureDateDifferenceString}
      />
    );
  };

  return {
    showUndoCancellationCTA: isEligibleSubscription,
    subscription,
    restorationEligibilityEndsAt,
    nextBillingDate,
    getCancelledStatusCopyOnXdp,
    monthlySubscriptionPrice,
    eligibleSubscriptionIds,
  };
};
